import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

// Hero görselleri
import hero1 from '../../assets/hero/hero_1.jpg';
import hero2 from '../../assets/hero/hero_2.jpg';
import hero3 from '../../assets/hero/hero3.jpg';
import hero4 from '../../assets/hero/hero4.jpg';
import hero5 from '../../assets/hero/hero5.jpg';
import hero6 from '../../assets/hero/hero6.jpg';

const Hero = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [hero1, hero2, hero3, hero4, hero5, hero6];

  // Otomatik görsel değişimi
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // 5 saniyede bir değişir

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const memberFlags = [
    { name: 'Türkiye', flag: FLAGS.turkey },
    { name: language === 'tr' ? 'Azerbaycan' : 'Azerbaijan', flag: FLAGS.azerbaijan },
    { name: language === 'tr' ? 'Kazakistan' : 'Kazakhstan', flag: FLAGS.kazakhstan },
    { name: language === 'tr' ? 'Kırgızistan' : 'Kyrgyzstan', flag: FLAGS.kyrgyzstan },
    { name: language === 'tr' ? 'Özbekistan' : 'Uzbekistan', flag: FLAGS.uzbekistan },
  ];

  const gradientOverlay = isDarkMode
    ? `linear-gradient(180deg, 
        rgba(13, 13, 26, 0.85) 0%, 
        rgba(26, 26, 46, 0.8) 30%, 
        rgba(45, 45, 68, 0.85) 60%,
        rgba(13, 13, 26, 0.95) 100%)`
    : `linear-gradient(180deg, 
        rgba(0, 171, 194, 0.85) 0%, 
        rgba(0, 138, 156, 0.75) 15%, 
        rgba(0, 107, 122, 0.8) 30%,
        rgba(26, 26, 46, 0.9) 60%,
        rgba(26, 26, 46, 0.98) 100%)`;

  return (
    <section id="home" style={styles.hero}>
      {/* Arka Plan Görselleri */}
      <div style={styles.backgroundContainer}>
        {heroImages.map((image, index) => (
          <div
            key={index}
            style={{
              ...styles.backgroundImage,
              backgroundImage: `url(${image})`,
              opacity: currentImageIndex === index ? 1 : 0,
            }}
          />
        ))}
      </div>

      {/* Gradient Overlay */}
      <div style={{
        ...styles.gradientOverlay,
        background: gradientOverlay,
      }} />

      {/* Motif Pattern Overlay - Güncellendi */}
      <div style={styles.patternOverlay}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          style={styles.patternSvg}
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern id="turkicPattern" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
              {/* Türk Motifleri - Geometrik Desenler */}
              <path 
                d="M60 0L120 60L60 120L0 60Z" 
                fill="none" 
                stroke="rgba(255,255,255,0.04)" 
                strokeWidth="1"
              />
              <path 
                d="M60 20L100 60L60 100L20 60Z" 
                fill="none" 
                stroke="rgba(255,255,255,0.03)" 
                strokeWidth="1"
              />
              <circle 
                cx="60" 
                cy="60" 
                r="8" 
                fill="rgba(255,255,255,0.02)"
              />
              {/* Köşe Motifleri */}
              <path 
                d="M0 0L30 0L0 30Z" 
                fill="rgba(255,255,255,0.015)"
              />
              <path 
                d="M120 0L120 30L90 0Z" 
                fill="rgba(255,255,255,0.015)"
              />
              <path 
                d="M0 120L0 90L30 120Z" 
                fill="rgba(255,255,255,0.015)"
              />
              <path 
                d="M120 120L90 120L120 90Z" 
                fill="rgba(255,255,255,0.015)"
              />
              {/* Merkez Yıldız */}
              <path 
                d="M60 45L65 55L75 55L67 62L70 72L60 66L50 72L53 62L45 55L55 55Z" 
                fill="rgba(255,255,255,0.025)"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#turkicPattern)" />
        </svg>
      </div>

      {/* İçerik */}
      <div style={styles.content}>
        <div style={{
          ...styles.badge,
          backgroundColor: isDarkMode ? `${DARK_THEME.primary}20` : 'rgba(255, 255, 255, 0.15)',
          borderColor: isDarkMode ? DARK_THEME.primary : 'rgba(255, 255, 255, 0.3)',
        }}>
          <span>{language === 'tr' ? '🌍 Türk Devletlerini Bir Araya Getiriyoruz' : '🌍 Uniting Turkic Nations'}</span>
        </div>
        <h1 style={styles.title}>
          {language === 'tr' ? 'Modern' : 'The Modern'} <span style={{...styles.highlight, color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary}}>{language === 'tr' ? 'İpek Yolu' : 'Silk Road'}</span>
        </h1>
        <p style={styles.subtitle}>
          {language === 'tr'
            ? 'Azerbaycan, Kazakistan, Kırgızistan, Türkiye ve Özbekistan’ı ortak miras, kültür ve ekonomik iş birliği yoluyla birbirine bağlıyoruz'
            : 'Connecting Azerbaijan, Kazakhstan, Kyrgyzstan, Türkiye & Uzbekistan through shared heritage, culture, and economic cooperation'}
        </p>
        
        {/* Member Flags */}
        <div style={styles.flagsContainer}>
          {memberFlags.map((item, index) => (
            <div key={index} style={styles.flagItem}>
              <img src={item.flag} alt={item.name} style={styles.flagImage} />
              <span style={styles.flagName}>{item.name}</span>
            </div>
          ))}
        </div>

        <div style={{
          ...styles.stats,
          backgroundColor: isDarkMode ? `${DARK_THEME.surface}90` : 'rgba(255, 255, 255, 0.1)',
          borderColor: isDarkMode ? DARK_THEME.border : 'rgba(255, 255, 255, 0.15)',
        }}>
          <div style={styles.statItem}>
            <span style={{...styles.statNumber, color: isDarkMode ? DARK_THEME.primary : COLORS.secondary}}>5+</span>
            <span style={styles.statLabel}>{language === 'tr' ? 'Üye Devlet' : 'Member States'}</span>
          </div>
          <div style={styles.statDivider}></div>
          <div style={styles.statItem}>
            <span style={{...styles.statNumber, color: isDarkMode ? DARK_THEME.primary : COLORS.secondary}}>170M+</span>
            <span style={styles.statLabel}>{language === 'tr' ? 'Nüfus' : 'Population'}</span>
          </div>
          <div style={styles.statDivider}></div>
          <div style={styles.statItem}>
            <span style={{...styles.statNumber, color: isDarkMode ? DARK_THEME.primary : COLORS.secondary}}>4.5M</span>
            <span style={styles.statLabel}>{language === 'tr' ? 'km² Yüzölçümü' : 'km² Territory'}</span>
          </div>
        </div>
        <div style={styles.buttons}>
          <Link to="/about" style={{
            ...styles.primaryButton,
            backgroundColor: isDarkMode ? DARK_THEME.primary : COLORS.secondary,
            color: isDarkMode ? COLORS.dark : COLORS.dark,
          }}>{language === 'tr' ? 'TDT’yi Keşfedin' : 'Explore OTS'}</Link>
          <Link to="/member-states" style={{
            ...styles.secondaryButton,
            borderColor: isDarkMode ? DARK_THEME.primary : 'rgba(255, 255, 255, 0.5)',
            color: isDarkMode ? DARK_THEME.primary : COLORS.white,
          }}>{language === 'tr' ? 'Üye Devletler' : 'Member States'}</Link>
        </div>
      </div>

      {/* Görsel İndikatörleri - Sağda dikey */}
      <div style={styles.imageIndicators}>
        {heroImages.map((_, index) => (
          <button
            key={index}
            style={{
              ...styles.indicator,
              backgroundColor: currentImageIndex === index 
                ? (isDarkMode ? DARK_THEME.primary : COLORS.white) 
                : 'rgba(255, 255, 255, 0.4)',
              transform: currentImageIndex === index ? 'scale(1.3)' : 'scale(1)',
              width: currentImageIndex === index ? '12px' : '10px',
              height: currentImageIndex === index ? '12px' : '10px',
            }}
            onClick={() => setCurrentImageIndex(index)}
            aria-label={language === 'tr' ? `${index + 1}. görsele git` : `Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div style={styles.scrollIndicator}>
        <span style={styles.scrollText}>{language === 'tr' ? 'Keşfetmek için kaydırın' : 'Scroll to explore'}</span>
        <div style={styles.scrollArrow}>↓</div>
      </div>
    </section>
  );
};

const styles = {
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
    padding: '120px 20px 80px',
    overflow: 'hidden',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transition: 'opacity 1.5s ease-in-out',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  patternOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Cpath d='M60 0L120 60L60 120L0 60Z' fill='none' stroke='rgba(255,255,255,0.08)' stroke-width='1'/%3E%3Cpath d='M60 20L100 60L60 100L20 60Z' fill='none' stroke='rgba(255,255,255,0.03)' stroke-width='1'/%3E%3Ccircle cx='60' cy='60' r='8' fill='rgba(255,255,255,0.02)'/%3E%3Cpath d='M0 0L30 0L0 30Z' fill='rgba(255,255,255,0.015)'/%3E%3Cpath d='M120 0L120 30L90 0Z' fill='rgba(255,255,255,0.015)'/%3E%3Cpath d='M0 120L0 90L30 120Z' fill='rgba(255,255,255,0.015)'/%3E%3Cpath d='M120 120L90 120L120 90Z' fill='rgba(255,255,255,0.015)'/%3E%3Cpath d='M60 45L65 55L75 55L67 62L70 72L60 66L50 72L53 62L45 55L55 55Z' fill='rgba(255,255,255,0.025)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
    zIndex: 2,
    opacity: 1,
  },
  content: {
    position: 'relative',
    zIndex: 3,
    maxWidth: '900px',
  },
  badge: {
    display: 'inline-block',
    border: '1px solid',
    padding: '10px 25px',
    borderRadius: '50px',
    marginBottom: '30px',
    color: COLORS.white,
    fontSize: '14px',
    fontWeight: '500',
    backdropFilter: 'blur(10px)',
  },
  title: {
    fontSize: 'clamp(40px, 8vw, 72px)',
    color: COLORS.white,
    fontWeight: '700',
    lineHeight: '1.1',
    marginBottom: '25px',
    textShadow: '0 2px 20px rgba(0,0,0,0.3)',
  },
  highlight: {
    display: 'block',
  },
  subtitle: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.9)',
    lineHeight: '1.7',
    maxWidth: '650px',
    margin: '0 auto 30px',
    textShadow: '0 1px 10px rgba(0,0,0,0.2)',
  },
  flagsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '25px',
    marginBottom: '35px',
    flexWrap: 'wrap',
  },
  flagItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  flagImage: {
    height: '40px',
    width: 'auto',
    borderRadius: '4px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    transition: 'transform 0.3s ease',
  },
  flagName: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '500',
  },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '30px',
    marginBottom: '40px',
    flexWrap: 'wrap',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '25px 40px',
    border: '1px solid',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: '700',
  },
  statLabel: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.8)',
    marginTop: '5px',
  },
  statDivider: {
    width: '1px',
    height: '40px',
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  buttons: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    padding: '16px 40px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'inline-block',
  },
  secondaryButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    padding: '16px 40px',
    fontSize: '16px',
    fontWeight: '600',
    border: '2px solid',
    borderRadius: '50px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    backdropFilter: 'blur(10px)',
  },
  imageIndicators: {
    position: 'absolute',
    right: '30px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    zIndex: 4,
  },
  indicator: {
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: 0,
    backgroundColor: 'transparent',
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.6)',
    zIndex: 4,
  },
  scrollText: {
    fontSize: '12px',
    marginBottom: '10px',
  },
};

export default Hero;
